/**
 * Config-driven App-mode subsystems: subsystem nodes from the designer tree via
 * GET /api/v1/designer/subsystems. The configuration is the source of truth for
 * the generated command interface.
 */
import { useQuery } from 'react-query';

import { TokenManager } from '@/utils/api';
import { useAppLocale } from '@/app/permissions/useAppLocale';
import type { Subsystem } from './subsystemNav';

export type { Subsystem, SubsystemGroup, SubsystemItem } from './subsystemNav';
export { subsystemsToNavItems } from './subsystemNav';

export interface SubsystemsPayload {
  subsystems: Subsystem[];
  hasSubsystemNodes: boolean;
}

export function parseSubsystemsPayload(ok: boolean, payload: unknown): SubsystemsPayload {
  if (!ok) return { subsystems: [], hasSubsystemNodes: false };
  if (!payload || typeof payload !== 'object') {
    return { subsystems: [], hasSubsystemNodes: false };
  }
  const body = payload as { data?: unknown; has_subsystem_nodes?: unknown };
  return {
    subsystems: Array.isArray(body.data) ? (body.data as Subsystem[]) : [],
    hasSubsystemNodes: body.has_subsystem_nodes === true,
  };
}

export function useSubsystems(connectionId: string | undefined): {
  subsystems: Subsystem[];
  hasSubsystemNodes: boolean;
  isLoading: boolean;
} {
  const { locale } = useAppLocale();
  const { data, isLoading } = useQuery<SubsystemsPayload>(
    ['subsystems', connectionId, locale ?? ''],
    async () => {
      const token = TokenManager.getAccessToken();
      if (!token || !connectionId) return { subsystems: [], hasSubsystemNodes: false };
      const resp = await fetch(
        `/api/v1/designer/subsystems?connection_id=${encodeURIComponent(connectionId)}&locale=${encodeURIComponent(locale ?? '')}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = resp.ok ? await resp.json() : null;
      return parseSubsystemsPayload(resp.ok, payload);
    },
    { enabled: !!connectionId, staleTime: 30_000 },
  );
  return {
    subsystems: data?.subsystems || [],
    hasSubsystemNodes: data?.hasSubsystemNodes ?? false,
    isLoading,
  };
}
